import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

// Single-round-trip bootstrap: profile + roles + page visibility.
// Hydrates other queries' caches so downstream hooks don't re-fetch on mount.
export function useBootstrap() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["bootstrap", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_bootstrap" as never);
      if (error) throw error;
      return data as {
        profile: Record<string, unknown> | null;
        roles: string[];
        page_visibility: Record<string, boolean>;
      };
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!query.data) return;
    const { profile, page_visibility } = query.data;
    if (profile && user) qc.setQueryData(["profile", user.id], profile);
    if (page_visibility) {
      const rows = Object.entries(page_visibility).map(([page_key, is_enabled]) => ({
        page_key,
        is_enabled,
      }));
      qc.setQueryData(["page-visibility"], rows);
    }
  }, [query.data, user, qc]);

  return query;
}
